// The web half of the `mm-ticket-extract` contract.
//
// Mirrors `packages/api/src/services/miller-mize/mm-ticket-extract/
// mm-ticket-extract.shared.ts`. The two packages cannot import from each other,
// so the version is duplicated deliberately and checked on every response --
// borrowed from @intrasync/designer's PlanAssist contract, where the point is
// that skew between independently-deployed halves fails LOUDLY instead of
// quietly returning fields that have moved.
//
// Bump both sides together, or don't bump either.
// 3 (2026-09-15): rows carry the ticket's printed date, stored on the MARK.
// 4 (2026-09-18): rows carry the printed POUNDS and the page its density, so a
// cubic foot can be checked on tickets that print no line total.
// 5 (2026-09-18): the result says which checks the API build carries, and whether
// a page was read from the file's own text or from the image. Bumped on purpose
// even though the fields are additive -- v4 spanned several builds, so a
// half-deploy passed the version check in silence and read as a reader fault.
export const MM_TICKET_EXTRACT_CONTRACT_VERSION = 5;

/** Rows at or above this are shown plainly; below it they are flagged for a
 *  human look. Same threshold @intrasync/designer uses, so the two agree. */
export const REVIEW_CONFIDENCE_THRESHOLD = 0.8;

// There is deliberately no row type here. `ParsedMarkRow` in MMProductionStore
// carries the optional `confidence` and `sourcePage` fields, so a read row and
// a pasted row are the SAME type -- which is what lets one review grid and one
// bulk writer serve both paths.

/**
 * One thing the reader wants to say about a page. Mirrors `MmExtractMessage`
 * on the API side; v2 replaced the old flat `errors: string[]`.
 *
 * The page is NOT in the text. That was the bug: every message read `Page 7:
 * ...`, so one cause across sixty pages was sixty distinct strings, deduping
 * collapsed nothing, and the list was cut off at five with no way to see the
 * rest. Creede, 2026-09-04: "anytime there's more than five warnings, it just
 * says and more, and I can't see the more."
 */
export interface TicketExtractMessage {
  kind: "error" | "info";
  causeKey: string;
  text: string;
}

/** One cause, with every page it happened on. What the dialog actually shows. */
export interface GroupedMessage extends TicketExtractMessage {
  pages: number[];
}

/**
 * "3", "3-5", "3-5, 9". Consecutive runs collapse; gaps stay visible, because
 * a cause hitting pages 3-5 and a cause hitting 3 and 47 are different stories.
 */
export const formatPageRange = (pages: number[]): string => {
  const sorted = [...new Set(pages)].sort((a, b) => a - b);
  if (sorted.length === 0) return "";
  const runs: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];
  for (const page of sorted.slice(1)) {
    if (page === prev + 1) {
      prev = page;
      continue;
    }
    runs.push(start === prev ? `${start}` : `${start}\u2013${prev}`);
    start = page;
    prev = page;
  }
  runs.push(start === prev ? `${start}` : `${start}\u2013${prev}`);
  return runs.join(", ");
};
